import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { admin } from '../lib/api/admin.ts'
import type { AgentCommand } from '../lib/api/admin.ts'
import { webDefense } from '../lib/api/resources.ts'
import type { WebChallenge } from '../lib/api/types.ts'
import { isAwaitingAgent } from '../lib/commands/semantics.ts'
import { useCommandTracking } from '../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../lib/format.ts'
import { navigate, useSearchParams } from '../lib/router.tsx'
import { usePolledResource } from '../lib/usePolledResource.ts'
import { useResource } from '../lib/useResource.ts'
import { activityPresentation, availabilityOf, requestLine } from '../lib/webdefense/semantics.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { TenantSelect } from '../components/logs/Directory.tsx'
import { CommandStateBadge } from '../components/commands/CommandState.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { Pagination } from '../components/primitives/Pagination.tsx'
import { RelativeTime } from '../components/primitives/RelativeTime.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { ChallengeDrawer, ExpiresIn, IpStateBadge } from '../components/webdefense/ChallengeDrawer.tsx'

const PAGE_SIZE = 50

/** Fila pequena e com prazo de 24 h: vale reler sozinha, sem esperar o operador. */
const POLL_MS = 30_000

function filtersHref(companyId: string | null): string {
  return companyId === null ? '/challenges' : `/challenges?companyId=${encodeURIComponent(companyId)}`
}

/**
 * Challenges do Web Defense: IPs que o Argos não teve certeza de bloquear e que
 * esperam a decisão de um operador. Mais recentes primeiro.
 *
 * O IP em challenge **continua sendo atendido**, sob monitoramento reforçado —
 * um sinal forte nesse meio-tempo o bloqueia sozinho. Por isso a fila mostra
 * quanto falta para expirar e o que o IP fez desde que abriu: é o que separa um
 * cliente legítimo de alguém tateando a aplicação.
 *
 * O item só sai da fila quando o agente responde. Enquanto isso, a decisão
 * pedida nesta tela aparece "a caminho" na linha.
 */
export function Challenges() {
  const search = useSearchParams()
  // `tenant_id` da telemetria = id da empresa: o seletor de empresa serve aos dois.
  const companyId = useMemo(() => search.get('companyId')?.trim() || null, [search])
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<WebChallenge | null>(null)
  // Decisões pedidas nesta sessão, por challenge, até o agente responder.
  const [inFlight, setInFlight] = useState<Readonly<Record<string, AgentCommand>>>({})

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      webDefense.challenges({ companyId: companyId ?? undefined, page, pageSize: PAGE_SIZE }, signal),
    [companyId, page],
  )
  const { data, error, loading, reload, loadedAt } = usePolledResource(fetcher, POLL_MS)

  // O catálogo sabe o papel de quem pergunta e se este Athena já conhece o comando.
  const catalogFetcher = useCallback(
    (signal: AbortSignal) =>
      admin.commandCatalog(undefined, signal).catch((cause: unknown) => {
        if (signal.aborted) throw cause
        return null
      }),
    [],
  )
  const catalog = useResource(catalogFetcher)
  const availability = availabilityOf(catalog.data, 'resolve_challenge')

  const register = useCallback(
    (challengeId: string, command: AgentCommand) => setInFlight((prev) => ({ ...prev, [challengeId]: command })),
    [],
  )
  const answered = useCallback(
    (challengeId: string) => {
      setInFlight((prev) => {
        const next = { ...prev }
        delete next[challengeId]
        return next
      })
      reload()
    },
    [reload],
  )

  return (
    <>
      <PageHeader
        title="Challenges"
        actions={
          <>
            {loadedAt !== null && (
              <span className="tnum text-micro text-fg-muted">
                carregado às {new Date(loadedAt).toLocaleTimeString('pt-BR')}
              </span>
            )}
            <Button size="sm" onClick={reload} busy={loading && data === null}>
              Atualizar
            </Button>
          </>
        }
      />

      <Toolbar>
        <TenantSelect
          value={companyId ?? ''}
          onChange={(value) => {
            setPage(1)
            navigate(filtersHref(value || null))
          }}
        />
        {loading && data !== null && <span className="self-end pb-1 text-micro text-fg-muted">atualizando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto pt-2">
        {error !== null && data === null ? (
          <div className="px-3">
            <ErrorState error={error} onRetry={reload} />
          </div>
        ) : data === null ? (
          <LoadingRow label="Carregando a fila de challenges…" />
        ) : data.items.length === 0 ? (
          <EmptyState
            title={companyId === null ? 'Nenhum challenge pendente.' : 'Nenhum challenge pendente nesta empresa.'}
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>IP</th>
                {/* As colunas de texto truncam: sem largura, a tabela as espreme até o nada. */}
                <th scope="col" className={`${TH} w-[14%]`}>Máquina</th>
                <th scope="col" className={`${TH} w-[24%]`}>Regra</th>
                <th scope="col" className={`${TH} w-[20%]`}>Requisição</th>
                <th scope="col" className={TH}>Abriu</th>
                <th scope="col" className={TH}>Expira</th>
                <th scope="col" className={`${TH} text-right`}>Requisições</th>
                <th scope="col" className={TH}>Estado do IP</th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((challenge) => {
                const open = selected?.challenge_id === challenge.challenge_id
                const command = inFlight[challenge.challenge_id]
                return (
                  <ChallengeRow
                    key={`${challenge.endpoint_id}/${challenge.challenge_id}`}
                    challenge={challenge}
                    selected={open}
                    onOpen={() => setSelected(challenge)}
                    // Com a ficha aberta, quem acompanha é ela: um acompanhamento só por comando.
                    command={
                      command === undefined || open ? null : (
                        <InFlight command={command} onAnswered={() => answered(challenge.challenge_id)} />
                      )
                    }
                  />
                )
              })}
            </tbody>
          </table>
        )}
        {error !== null && data !== null && (
          <div className="px-3 py-2">
            <ErrorState error={error} onRetry={reload} />
          </div>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}

      {selected !== null && (
        <ChallengeDrawer
          key={`${selected.endpoint_id}/${selected.challenge_id}`}
          challenge={selected}
          unavailableReason={availability.reason}
          onClose={() => setSelected(null)}
          onResolved={() => answered(selected.challenge_id)}
          onCommand={(command) => register(selected.challenge_id, command)}
        />
      )}
    </>
  )
}

/** A decisão a caminho, acompanhada até o agente responder. */
function InFlight({ command, onAnswered }: { command: AgentCommand; onAnswered: () => void }) {
  const tracking = useCommandTracking(command.id, command)
  const state = tracking.command?.state ?? command.state
  const awaiting = isAwaitingAgent(state)

  // Respondeu (ou venceu): a fila relê e a linha deixa de mostrar o comando.
  useEffect(() => {
    if (!awaiting) onAnswered()
    // `onAnswered` muda de identidade a cada render da fila; o que importa é a transição.
  }, [awaiting])

  return awaiting ? (
    // Sem quebra: a coluna cresce, em vez de o selo vazar sobre o estado do IP.
    <span className="inline-flex flex-col items-end gap-0.5 whitespace-nowrap text-micro text-fg-muted">
      decisão a caminho
      <CommandStateBadge state={state} />
    </span>
  ) : null
}

export function ChallengeRow({
  challenge,
  selected,
  onOpen,
  command = null,
}: {
  challenge: WebChallenge
  selected: boolean
  onOpen: () => void
  /** Uma decisão desta sessão ainda sem resposta do agente. */
  command?: ReactNode
}) {
  const line = requestLine(challenge)
  const opened = Date.parse(challenge.opened_at)
  const activity = activityPresentation(challenge.activity)

  return (
    <tr className={`${TR} cursor-pointer ${selected ? 'bg-inset' : ''}`} onClick={onOpen}>
      <th scope="row" className={`${TD} text-left font-mono font-normal whitespace-nowrap text-fg`}>
        {challenge.ip}
      </th>
      <td className={`${TD} max-w-0`}>
        <span className="block truncate font-mono text-fg-secondary" title={challenge.endpoint_id}>
          {challenge.machine_name ?? challenge.endpoint_id.slice(0, 8)}
        </span>
      </td>
      <td className={`${TD} max-w-0`}>
        <span className="block truncate text-fg" title={challenge.rule_name ?? undefined}>
          {challenge.rule_name ?? challenge.rule_id ?? '—'}
        </span>
        {challenge.rule_id !== null && (
          <span className="block truncate font-mono text-micro text-fg-muted" title={challenge.rule_id}>
            {challenge.rule_id}
          </span>
        )}
      </td>
      <td className={`${TD} max-w-0`}>
        <span className="block truncate font-mono text-fg-secondary" title={line ?? undefined}>
          {line ?? '—'}
        </span>
        {challenge.host !== null && (
          <span className="block truncate font-mono text-micro text-fg-muted">{challenge.host}</span>
        )}
      </td>
      <td className={`${TD} whitespace-nowrap`}>
        <span className="tnum block">{formatDayClock(challenge.opened_at)}</span>
        <RelativeTime
          since={Number.isFinite(opened) ? opened : null}
          intervalMs={30_000}
          className="block text-micro text-fg-muted"
        />
      </td>
      <td className={`${TD} whitespace-nowrap`}>
        <ExpiresIn iso={challenge.expires_at} />
      </td>
      <td className={`${TD} text-right`} title={activity.breakdown ?? undefined}>
        <span className="tnum block">{activity.requests}</span>
        {activity.breakdown !== null && (
          <span className="block truncate text-micro text-fg-muted">{activity.breakdown}</span>
        )}
      </td>
      <td className={`${TD} whitespace-nowrap`}>
        <IpStateBadge state={challenge.activity.state} />
      </td>
      <td className={`${TD} text-right`}>
        {command ?? (
          <Button
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              onOpen()
            }}
          >
            Decidir
          </Button>
        )}
      </td>
    </tr>
  )
}
